<template>
  <b-container fluid v-if="sales_map_context_loaded" class="sales-map-container">
    <b-row class="m-0 mb-3">
      <b-col class="px-0">
        <div class="map-controls-bar white-box">
          <div class="d-flex align-items-center flex-wrap controls-content">
            <div class="d-flex align-items-center control-group">
              <span class="control-label">Métricas:</span>
              <div class="metrics-select-wrapper" ref="metricsDropdown">
                <button 
                  type="button"
                  class="metrics-select-button"
                  @click="handleMetricsButtonClick"
                  :class="{ 'is-open': metricsDropdownOpen }">
                  <span class="metrics-select-text">{{ selectedMetricLabel }}</span>
                  <span class="metrics-select-arrow" :class="{ 'is-open': metricsDropdownOpen }">▼</span>
                </button>
                <div 
                  v-if="metricsDropdownOpen" 
                  class="metrics-select-menu"
                  @click.stop="handleMenuClick">
                  <div 
                    v-for="(metric, index) in availableMetrics" 
                    :key="index"
                    class="metrics-select-option"
                    :class="{ 'is-selected': selectedMetric === metric.value }"
                    @click.stop="handleOptionClick(metric, $event)">
                    {{ metric.label }}
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex align-items-center control-group">
              <span class="control-label">Data:</span>
              <div class="date-select-wrapper" ref="dateDropdown">
                <button 
                  type="button"
                  class="date-select-button"
                  @click="handleDateButtonClick"
                  :class="{ 'is-open': dateDropdownOpen }">
                  <span class="date-select-text">{{ dateFilterLabel }}</span>
                  <span class="date-select-arrow" :class="{ 'is-open': dateDropdownOpen }">▼</span>
                </button>
                <div 
                  v-if="dateDropdownOpen" 
                  class="date-select-menu"
                  @click.stop="handleDateMenuClick">
                  <div 
                    v-for="(option, index) in datePeriodOptions" 
                    :key="index"
                    class="date-select-option"
                    :class="{ 'is-selected': selectedDatePeriod === option.value }"
                    @click.stop="handleDatePeriodClick(option, $event)">
                    {{ option.label }}
                  </div>
                </div>
              </div>
              <date-picker 
                v-if="showDatePicker" 
                @date-selected="handleDateRangeSelected">
              </date-picker>
            </div>
            <div class="d-flex align-items-center control-group">
              <label for="mouse-function" class="control-option">
                <input type="checkbox" id="mouse-function" name="mouse-function" v-model="showTooltip">
                <span>Exibir perfil de clientes no passar do mouse</span>
              </label>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-row class="m-0 flex-grow-1" style="min-height: 0; width: 100%; max-width: 100%; overflow: hidden;">
      <b-col md="12" class="px-0 d-flex justify-content-center align-items-center desktop-resize-col" style="min-height: 0; max-width: 100%; overflow: hidden;">
        <div class="map-core">
          <div class="map-body">
            <div class="zoom-controls">
              <button 
                class="zoom-btn zoom-in" 
                @click="zoomIn"
                :disabled="zoomLevel >= maxZoom"
                title="Aumentar zoom">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="11" y1="8" x2="11" y2="14"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
              <button 
                class="zoom-btn zoom-out" 
                @click="zoomOut"
                :disabled="zoomLevel <= minZoom"
                title="Diminuir zoom">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </button>
            </div>
            
            <svg 
              id="svg-map" 
              xmlns="http://www.w3.org/2000/svg" 
              xmlns:xlink="http://www.w3.org/1999/xlink" 
              :viewBox="currentViewBox" 
              preserveAspectRatio="xMidYMid meet"
              :style="{ zIndex: 2, maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', cursor: isDragging ? 'grabbing' : 'grab' }"
              @click.stop="handleMapClick"
              @mousemove="handleMapMove"
              @mouseleave="handleMapLeave"
              @mousedown.stop="startPan"
              @mouseup.stop="endPan">
              <rect id="backgroundrect" width="100%" height="100%" x="0" y="0" fill="none" stroke="none"/>
              <g class="currentLayer" v-html="brazilMapSvg"></g>
            </svg>

            <div 
              v-if="hoveredState" 
              class="map-tooltip"
              :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
              <div class="tooltip-title">
                {{ getStateNameFromCode(hoveredState.code) }}
              </div>
              <div v-if="stateDetailsLoading" class="tooltip-loading">
                <div class="spinner"></div>
                <span>Carregando…</span>
              </div>
              <div v-else>
                <div v-if="showTooltip && stateCustomerProfile" class="tooltip-section customer-profile-section">
                  <div class="section-title">Perfil de Clientes</div>
                  
                  <div class="gender-bars">
                    <div 
                      class="gender-bar male-bar" 
                      :style="{ width: stateCustomerProfile.males.percentage + '%' }">
                      <img src="/images/male_icon.svg" alt="male" class="gender-icon">
                      <span class="gender-info">
                        {{ stateCustomerProfile.males.quantity }} / {{ stateCustomerProfile.males.percentage }}%
                      </span>
                    </div>
                    <div 
                      class="gender-bar female-bar" 
                      :style="{ width: stateCustomerProfile.females.percentage + '%' }">
                      <img src="/images/female_icon.svg" alt="female" class="gender-icon">
                      <span class="gender-info">
                        {{ stateCustomerProfile.females.quantity }} / {{ stateCustomerProfile.females.percentage }}%
                      </span>
                    </div>
                  </div>
                  
                  <div class="age-groups-tooltip">
                    <div 
                      v-for="(ageGroup, index) in stateCustomerProfile.ageGroups" 
                      :key="index"
                      class="age-group-tooltip">
                      <div class="age-bars-tooltip">
                        <div class="age-bar-tooltip">
                          <div 
                            class="age-bar-fill male-color" 
                            :style="{ height: Math.max(ageGroup.male.percentage, 5) + '%' }">
                            <span class="age-value">{{ ageGroup.male.value }}</span>
                          </div>
                        </div>
                        <div class="age-bar-tooltip">
                          <div 
                            class="age-bar-fill female-color" 
                            :style="{ height: Math.max(ageGroup.female.percentage, 5) + '%' }">
                            <span class="age-value">{{ ageGroup.female.value }}</span>
                          </div>
                        </div>
                      </div>
                      <div class="age-label-tooltip">{{ ageGroup.label }} anos</div>
                    </div>
                  </div>
                </div>
                
                <template v-if="!showTooltip">
                  <div class="tooltip-section">
                    <div class="section-title">Top 5 fretes escolhidos</div>
                    <div class="row-item" v-for="(item, idx) in topShippingTop5" :key="'ship-'+idx">
                      <span class="item-name">{{ item.name }}</span>
                      <span class="item-qty">{{ item.quantity }}</span>
                    </div>
                  </div>
                  
                  <div class="tooltip-section" v-if="topProductsTop5.length">
                    <div class="section-title">Top 5 produtos vendidos</div>
                    <div class="row-item" v-for="(item, idx) in topProductsTop5" :key="'prod-'+idx">
                      <span class="item-name">{{ truncateText(item.name, 36) }}</span>
                      <span class="item-qty">{{ item.quantity }}</span>
                    </div>
                  </div>
                  
                  <div class="tooltip-section" v-if="topCitiesTop5.length">
                    <div class="section-title">Top 5 cidades</div>
                    <div class="row-item" v-for="(item, idx) in topCitiesTop5" :key="'city-'+idx">
                      <span class="item-name">{{ item.name }}</span>
                      <span class="item-qty">{{ item.quantity }}</span>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <b-col lg="4" md="12" class="px-0 d-flex justify-content-end sidebar-col" style="min-height: 0; max-width: 100%;">
        <div id="sidebar" class="map-sidebar">
          <h4 class="title-text">Mapa Nacional</h4>
          <h5 class="sub-title-text">{{ mapTitle }}</h5>
          <p>Total de pedidos neste mapa:&nbsp; 
            <span class="chip green white-text small">{{ totalOrders }}</span>
          </p>

          <hr v-if="appliedFilters && appliedFilters.length > 0">

          <div v-if="appliedFilters && appliedFilters.length > 0">
            <input 
              class="flip sub-title-text" 
              value="Filtros aplicados" 
              @click="togglePanel('filters-panel')" 
              type="button">
            <ul id="filters-panel" :class="{ 'panel-hidden': !panelsVisible.filters }">
              <li v-for="(filter, index) in appliedFilters" :key="index" class="filter-item">
                <span class="normal-text">{{ filter.name }}: {{ filter.value }}</span>
                <button 
                  v-if="filter.type === 'region'"
                  @click="removeRegionFilter(filter.region)"
                  class="remove-filter-btn"
                  type="button">×</button>
              </li>
            </ul>
            <hr>
          </div>

          <div v-if="regions && regions.length > 0">
            <input 
              class="flip sub-title-text" 
              value="Regiões" 
              @click="togglePanel('regions-panel')" 
              type="button">
            <div id="regions-panel" :class="{ 'panel-hidden': !panelsVisible.regions }" class="regions-container">
              <div 
                v-for="(region, index) in regions" 
                :key="index"
                class="region-item"
                :class="{ 'region-selected': isRegionSelected(region) }"
                @click="toggleRegion(region)">
                <div class="region-color-indicator" :style="{ backgroundColor: region.color }"></div>
                <div class="region-info">
                  <span class="region-name">{{ region.name }}</span>
                  <span class="region-value">{{ region.value }}</span>
                </div>
                <div class="region-check" v-if="isRegionSelected(region)">✓</div>
              </div>
            </div>
            <hr>
          </div>

          <div>
            <input 
              class="flip sub-title-text" 
              value="Perfil geral de clientes"
              @click="togglePanel('customer-panel')" 
              type="button">
            <div id="customer-panel" class="gg-sidebar" :class="{ 'panel-hidden': !panelsVisible.customer }">
              <div class="flex-box gender-bars-sidebar">
                <div 
                  class="gender-bar-sidebar male-bar-sidebar">
                  <img src="/images/male_icon.svg" alt="male" class="gender-icon-sidebar">
                  <span class="gender-info-sidebar">
                    {{ customerProfile.males.quantity }} / {{ customerProfile.males.percentage }}%
                  </span>
                </div>
                <div 
                  class="gender-bar-sidebar female-bar-sidebar">
                  <img src="/images/female_icon.svg" alt="female" class="gender-icon-sidebar">
                  <span class="gender-info-sidebar">
                    {{ customerProfile.females.quantity }} / {{ customerProfile.females.percentage }}%
                  </span>
                </div>
              </div>

              <div style="margin-top: 25px; justify-content: space-evenly;" class="flex-box">
                <div 
                  v-for="(ageGroup, index) in customerProfile.ageGroups" 
                  :key="index"
                  class="age-pair">
                  <div class="pair-container">
                    <div class="age-bar">
                      <div 
                        class="age male-color" 
                        :style="{ height: Math.max(ageGroup.male.percentage, 5) + '%' }">
                        <span 
                          :class="['age-info', { 'rotate-age': ageGroup.male.rotate }]">
                          {{ ageGroup.male.value }}
                        </span>
                      </div>
                    </div>
                    <div class="age-bar">
                      <div 
                        class="age female-color" 
                        :style="{ height: Math.max(ageGroup.female.percentage, 5) + '%' }">
                        <span 
                          :class="['age-info', { 'rotate-age': ageGroup.female.rotate }]">
                          {{ ageGroup.female.value }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="age-container">
                    <div class="age-label">
                      <strong>{{ ageGroup.label }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <hr>
          </div>

          <div v-if="topShippingServices && topShippingServices.length > 0">
            <input 
              class="flip sub-title-text" 
              :value="topShippingTitle" 
              @click="togglePanel('top-ten-shipping-panel')" 
              type="button">
            <ul id="top-ten-shipping-panel" :class="{ 'panel-hidden': !panelsVisible.shipping }">
              <li v-for="(service, index) in topShippingServices" :key="index">
                <span class="small-text">{{ truncateText(service.name, 60) }}</span>
                <span class="small-text" style="float: right; padding-left: 20px;">{{ service.quantity }}</span>
              </li>
            </ul>
            <hr>
          </div>

          <div>
            <input 
              class="flip sub-title-text" 
              value="Top 10 produtos vendidos" 
              @click="togglePanel('top-ten-products-panel')" 
              type="button">
            <ul id="top-ten-products-panel" :class="{ 'panel-hidden': !panelsVisible.products }">
              <template v-if="topProducts && topProducts.length > 0">
                <li v-for="(product, index) in topProducts" :key="index">
                  <span class="small-text">{{ truncateText(product.name, 60) }}</span>
                  <span class="small-text" style="float: right; padding-left: 20px;">{{ product.quantity }}</span>
                </li>
              </template>
              <li v-else class="small-text" style="color: #999;">Nenhum produto encontrado</li>
            </ul>
            <hr>
          </div>

          <div v-if="topCities && topCities.length > 0">
            <input 
              class="flip sub-title-text" 
              :value="topCitiesTitle" 
              @click="togglePanel('top-ten-city-panel')" 
              type="button">
            <ul id="top-ten-city-panel" :class="{ 'panel-hidden': !panelsVisible.cities }">
              <li v-for="(city, index) in topCities" :key="index">
                <span class="small-text">{{ truncateText(city.name, 60) }}</span>
                <span class="small-text" style="float: right; padding-left: 20px;">{{ city.quantity }}</span>
              </li>
            </ul>
            <hr>
          </div>

          <div>
            <input 
              class="flip sub-title-text" 
              value="Top 10 Estados" 
              @click="togglePanel('top-ten-states-panel')" 
              type="button">
            <ul id="top-ten-states-panel" :class="{ 'panel-hidden': !panelsVisible.states }">
              <template v-if="topStates && topStates.length > 0">
                <li v-for="(state, index) in topStates" :key="index">
                  <span class="small-text">{{ truncateText(state.name, 60) }}</span>
                  <span class="small-text" style="float: right; padding-left: 20px;">{{ state.quantity }}</span>
                </li>
              </template>
              <li v-else class="small-text" style="color: #999;">Nenhum estado encontrado</li>
            </ul>
            <hr>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
  <loader-box v-else height="400px" class="mb-4 mr-2 ml-2"/>
</template>

<script>
import LoaderBox from '@/components/LoaderBox.vue'
import DatePicker from '@/components/DatePicker.vue'
import { brazilMapSvg } from './brazil-map-svg.js'
import ApiService from '@/services/api.service.js'
import axios from 'axios'

export default {
  name: 'SalesMap',
  components: {
    LoaderBox,
    DatePicker
  },
  data() {
    return {
      clickFunction: '1',
      showTooltip: false,
      panelsVisible: {
        filters: true,
        regions: true,
        metricsFilters: false,
        customer: false,
        shipping: false,
        products: false,
        cities: false,
        states: false,
        dateFilter: false
      },
      dateFilter: {
        period: 6, // Mês atual por padrão
        year: '',
        month: '',
        day: '',
        week: '',
        dateStart: '',
        dateEnd: ''
      },
      selectedDatePeriod: 6, // Mês atual por padrão
      showDatePicker: false,
      datePeriodOptions: [
        { value: 1, label: 'Hoje', period: 1 },
        { value: 2, label: 'Ontem', period: 2 },
        { value: 3, label: '7 dias', period: 3 },
        { value: 4, label: '15 dias', period: 4 },
        { value: 6, label: 'Mês atual', period: 6 },
        { value: 7, label: 'Mês passado', period: 7 },
        { value: 8, label: 'Ano atual', period: 8 },
        { value: 9, label: '1 ano', period: 9 },
        { value: 10, label: 'Intervalo de datas', period: 10 }
      ],
      hoveredState: null,
      tooltip: {
        visible: false,
        x: 0,
        y: 0
      },
      stateDetailsCache: {},
      stateDetailsLoading: false,
      isLoadingAllStates: false,
      allStatesLoaded: false,
      selectedRegions: [],
      selectedMetric: 'percentage',
      sidebarObserver: null,
      metricsDropdownOpen: false,
      dateDropdownOpen: false,
      defaultViewBox: { x: 0, y: 0, width: 1022, height: 840 },
      currentViewBox: '0 0 1022 840',
      zoomLevel: 1,
      minZoom: 1,
      maxZoom: 3,
      zoomStep: 0.2,
      isDragging: false,
      panStart: { x: 0, y: 0, time: 0 },
      wasDragging: false,
      viewBoxValues: { x: 0, y: 0, width: 1022, height: 840 },
      availableMetrics: [
        { 
          value: 'percentage', 
          label: 'Porcentagem da quantidade total de pedidos', 
          icon: '📊',
          endpoint: 'percentage'
        },
        { 
          value: 'total_count', 
          label: 'Quantidade total de pedidos', 
          icon: '🔢',
          endpoint: 'total_count'
        },
        { 
          value: 'total_sales', 
          label: 'Valor de venda total', 
          icon: '💰',
          endpoint: 'total_sales'
        },
        { 
          value: 'sales_ticket', 
          label: 'Ticket médio de venda', 
          icon: '🎫',
          endpoint: 'sales_ticket'
        },
        { 
          value: 'total_shipping_cost', 
          label: 'Custo de envio total', 
          icon: '🚚',
          endpoint: 'total_shipping_cost'
        },
        { 
          value: 'shipping_cost_ticket', 
          label: 'Ticket médio de envio', 
          icon: '📦',
          endpoint: 'shipping_cost_ticket'
        }
      ]
    }
  },
  computed: {
    brazilMapSvg() {
      return this.$store.getters.sales_map_svg_content || brazilMapSvg
    },
    sales_map_context_loaded() {
      return this.$store.getters.sales_map_context_loaded || false
    },
    mapTitle() {
      return this.$store.getters.sales_map_title || 'Mapa de Vendas'
    },
    totalOrders() {
      return this.$store.getters.sales_map_total_orders || 0
    },
    appliedFilters() {
      const storeFilters = this.$store.getters.sales_map_applied_filters || []
      // Remove o filtro "Arquivado" da lista
      const filteredStoreFilters = storeFilters.filter(filter => {
        const filterName = (filter.name || '').toLowerCase()
        return !filterName.includes('arquivado') && !filterName.includes('período')
      })
      
      // Adiciona o filtro de período se houver um selecionado
      const periodFilters = []
      if (this.selectedDatePeriod) {
        const periodOption = this.datePeriodOptions.find(opt => opt.value === this.selectedDatePeriod)
        if (periodOption) {
          let periodValue = periodOption.label
          
          // Se for intervalo de datas, mostra as datas formatadas
          if (this.selectedDatePeriod === 10 && this.dateFilter.dateStart) {
            if (this.dateFilter.dateEnd) {
              periodValue = `${this.formatDate(this.dateFilter.dateStart)} ~ ${this.formatDate(this.dateFilter.dateEnd)}`
            } else {
              periodValue = this.formatDate(this.dateFilter.dateStart)
            }
          }
          
          periodFilters.push({
            name: 'Período',
            value: periodValue,
            type: 'period'
          })
        }
      }
      
      const regionFilters = this.selectedRegions.map(region => ({
        name: 'Região',
        value: region.name,
        type: 'region',
        region: region
      }))
      return [...filteredStoreFilters, ...periodFilters, ...regionFilters]
    },
    availableYears() {
      const currentYear = new Date().getFullYear()
      const years = []
      // Gera anos dos últimos 5 anos até o próximo ano
      for (let i = currentYear - 5; i <= currentYear + 1; i++) {
        years.push(i)
      }
      return years.reverse()
    },
    hasDateFilter() {
      return !!(this.selectedDatePeriod || this.dateFilter.dateStart || this.dateFilter.dateEnd)
    },
    dateFilterLabel() {
      if (!this.selectedDatePeriod) {
        return 'Selecione a data'
      }
      
      // Se for intervalo de datas, mostra as datas
      if (this.selectedDatePeriod === 10 && this.dateFilter.dateStart) {
        if (this.dateFilter.dateEnd) {
          return `${this.formatDate(this.dateFilter.dateStart)} ~ ${this.formatDate(this.dateFilter.dateEnd)}`
        }
        return this.formatDate(this.dateFilter.dateStart)
      }
      
      // Caso contrário, mostra o label da opção selecionada
      const option = this.datePeriodOptions.find(opt => opt.value === this.selectedDatePeriod)
      return option ? option.label : 'Selecione a data'
    },
    regions() {
      return this.$store.getters.sales_map_regions || []
    },
    customerProfile() {
      return this.$store.getters.sales_map_customer_profile || {
        males: { quantity: 0, percentage: 0 },
        females: { quantity: 0, percentage: 0 },
        ageGroups: []
      }
    },
    topShippingServices() {
      return this.$store.getters.sales_map_top_shipping_services || []
    },
    topShippingTitle() {
      return this.$store.getters.sales_map_top_shipping_title || 'Top 10 Serviços de Entrega'
    },
    topProducts() {
      const products = this.$store.getters.sales_map_top_products || []
      if (!Array.isArray(products)) return []
      
      // Normalizar os dados para garantir formato consistente (name e quantity)
      return products.slice(0, 10).map(item => ({
        name: item.name || item.label || item.product_name || 'Produto',
        quantity: Number(item.count || item.quantity || item.total || 0)
      }))
    },
    topProductsTitle() {
      return this.$store.getters.sales_map_top_products_title || 'Top 10 produtos vendidos'
    },
    topCities() {
      return this.$store.getters.sales_map_top_cities || []
    },
    topCitiesTitle() {
      return this.$store.getters.sales_map_top_cities_title || 'Top 10 Cidades'
    },
    topStates() {
      const states = this.$store.getters.sales_map_top_states || []
      if (!Array.isArray(states)) return []
      
      // Normalizar os dados para garantir formato consistente (name e quantity)
      return states.slice(0, 10).map(item => ({
        name: item.name || '',
        quantity: Number(item.count || item.quantity || item.total || 0)
      }))
    },
    topStatesTitle() {
      return this.$store.getters.sales_map_top_states_title || 'Top 10 Estados'
    },
    stateValues() {
      return this.$store.getters.sales_map_state_values || {}
    },
    topShippingTop5() {
      const code = this.hoveredState && this.hoveredState.code
      if (!code) return []
      const storeMap = this.$store.getters.sales_map_states_details || {}
      const cacheKeyPrimary = `${code}_${this.selectedMetric}`
      const cacheKeyFallback = `${code}_all`
      const list = (storeMap[code] && storeMap[code].top_shippings)
        ? storeMap[code].top_shippings
        : (this.stateDetailsCache[cacheKeyPrimary] && this.stateDetailsCache[cacheKeyPrimary].top_shippings)
          ? this.stateDetailsCache[cacheKeyPrimary].top_shippings
          : (this.stateDetailsCache[cacheKeyFallback] && this.stateDetailsCache[cacheKeyFallback].top_shippings)
            ? this.stateDetailsCache[cacheKeyFallback].top_shippings
            : []
      return Array.isArray(list) ? list.slice(0, 5) : []
    },
    topProductsTop5() {
      const code = this.hoveredState && this.hoveredState.code
      if (!code) return []
      const storeMap = this.$store.getters.sales_map_states_details || {}
      const cacheKeyPrimary = `${code}_${this.selectedMetric}`
      const cacheKeyFallback = `${code}_all`
      const list = (storeMap[code] && storeMap[code].top_products)
        ? storeMap[code].top_products
        : (this.stateDetailsCache[cacheKeyPrimary] && this.stateDetailsCache[cacheKeyPrimary].top_products)
          ? this.stateDetailsCache[cacheKeyPrimary].top_products
          : (this.stateDetailsCache[cacheKeyFallback] && this.stateDetailsCache[cacheKeyFallback].top_products)
            ? this.stateDetailsCache[cacheKeyFallback].top_products
            : []
      return Array.isArray(list) ? list.slice(0, 5) : []
    },
    topCitiesTop5() {
      const code = this.hoveredState && this.hoveredState.code
      if (!code) return []
      const storeMap = this.$store.getters.sales_map_states_details || {}
      const cacheKeyPrimary = `${code}_${this.selectedMetric}`
      const cacheKeyFallback = `${code}_all`
      const list = (storeMap[code] && storeMap[code].top_cities)
        ? storeMap[code].top_cities
        : (this.stateDetailsCache[cacheKeyPrimary] && this.stateDetailsCache[cacheKeyPrimary].top_cities)
          ? this.stateDetailsCache[cacheKeyPrimary].top_cities
          : (this.stateDetailsCache[cacheKeyFallback] && this.stateDetailsCache[cacheKeyFallback].top_cities)
            ? this.stateDetailsCache[cacheKeyFallback].top_cities
            : []
      return Array.isArray(list) ? list.slice(0, 5) : []
    },
    selectedMetricLabel() {
      const metric = this.availableMetrics.find(m => m.value === this.selectedMetric)
      return metric ? metric.label : 'Selecione uma métrica'
    },
    stateCustomerProfile() {
      const code = this.hoveredState && this.hoveredState.code
      if (!code) return null
      
      const cacheKeyPrimary = `${code}_${this.selectedMetric}`
      const cacheKeyFallback = `${code}_all`
      const storeMap = this.$store.getters.sales_map_states_details || {}
      const stateDetails = storeMap[code] || this.stateDetailsCache[cacheKeyPrimary] || this.stateDetailsCache[cacheKeyFallback]
      
      if (stateDetails && stateDetails.customer_profile) {
        const profile = stateDetails.customer_profile
        return {
          males: {
            quantity: (profile.gender && profile.gender.male && profile.gender.male.quantity) || 0,
            percentage: parseFloat(((profile.gender && profile.gender.male && profile.gender.male.percentage) || '0').replace('%', '')) || 0
          },
          females: {
            quantity: (profile.gender && profile.gender.female && profile.gender.female.quantity) || 0,
            percentage: parseFloat(((profile.gender && profile.gender.female && profile.gender.female.percentage) || '0').replace('%', '')) || 0
          },
          ageGroups: (profile.age_groups || []).map(ageGroup => ({
            label: ageGroup.range || '',
            male: {
              value: (ageGroup.male && ageGroup.male.quantity) || 0,
              percentage: parseFloat((ageGroup.male && ageGroup.male.percentage || '0').replace('%', '')) || 0
            },
            female: {
              value: (ageGroup.female && ageGroup.female.quantity) || 0,
              percentage: parseFloat((ageGroup.female && ageGroup.female.percentage || '0').replace('%', '')) || 0
            }
          }))
        }
      }
      
      return null
    }
  },
  methods: {
    getStateValue(code) {
      const value = this.stateValues[code]
      if (value === null || value === undefined || value === '') {
        return 0
      }
      return value
    },
    getStateColorClass(code) {
      const value = this.getStateValue(code)
      if (!value || value === 0) return ''
      
      const northeast = ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE']
      const north = ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO']
      const midwest = ['DF', 'GO', 'MS', 'MT']
      const southeast = ['ES', 'MG', 'RJ', 'SP']
      const south = ['PR', 'RS', 'SC']

      if (northeast.includes(code)) return 'northeast-color'
      if (north.includes(code)) return 'north-color'
      if (midwest.includes(code)) return 'middlewestern-color'
      if (southeast.includes(code)) return 'southeast-color'
      if (south.includes(code)) return 'south-color'
      
      return ''
    },
    handleMapClick(event) {
      // Não processa clique se foi um arrasto
      if (this.wasDragging) {
        this.wasDragging = false
        return
      }
      
      // Proteção: garante que o sidebar não seja afetado por cliques no mapa
      if (typeof this.protectSidebar === 'function') {
        this.protectSidebar()
      } else {
        // Fallback caso o método não esteja disponível
        this.$nextTick(() => {
          const sidebar = document.getElementById('sidebar')
          if (sidebar) {
            sidebar.style.display = 'block'
            sidebar.style.visibility = 'visible'
            sidebar.style.opacity = '1'
          }
          const sidebarCol = document.querySelector('.sidebar-col')
          if (sidebarCol) {
            sidebarCol.style.display = 'flex'
            sidebarCol.style.visibility = 'visible'
          }
        })
      }
      
      // Previne propagação do evento para evitar efeitos colaterais
      event.stopPropagation()
      
      const stateElement = event.target.closest('a[id]')
      if (stateElement) {
        const stateId = stateElement.id
        const stateCode = this.getStateCodeFromId(stateId)
        const value = this.getStateValue(stateCode)
        if (!value || value <= 0) {
          return
        }
        if (this.clickFunction === '1') {
          this.exportStateData({ code: stateCode, id: stateId })
        } else {
          this.viewStateData({ code: stateCode, id: stateId })
        }
      }
    },
    handleMapMove(event) {
      if (this.isDragging) {
        this.handlePan(event)
        // Cancela qualquer hover enquanto arrasta
        this.hoveredState = null
        return
      }
      
      this.handleMapHover(event)
    },
    handleMapHover(event) {
      let stateElement = event.target.closest('a[id]')
      
      // Se não encontrou o estado diretamente, pode ser que o mouse esteja sobre um elemento externo
      if (!stateElement) {
        // Verifica se está sobre uma linha ou texto externo
        const externalLine = event.target.closest('line[id^="label-line-"]')
        const externalText = event.target.closest('text[id^="external-label-"]')
        const externalGroup = event.target.closest('g[id^="label-group-"]')
        
        if (externalLine || externalText || externalGroup) {
          const codeMatch = (externalLine && externalLine.id) || (externalText && externalText.id) || (externalGroup && externalGroup.id)
          if (codeMatch) {
            const codeLower = codeMatch.replace(/label-line-|external-label-|label-group-/g, '').split('-')[0]
            const stateCode = codeLower.toUpperCase()
            
            // Encontra o elemento do estado relacionado
            const stateIds = {
              'RN': 'rio_grande_do_norte',
              'SE': 'sergipe',
              'AL': 'alagoas',
              'PB': 'paraiba',
              'PE': 'pernambuco',
              'ES': 'espirito_santo',
              'RJ': 'rio_de_janeiro',
              'DF': 'distrito_federal'
            }
            
            const stateId = stateIds[stateCode]
            if (stateId) {
              stateElement = document.getElementById(stateId)
            }
          }
        }
      }
      
      if (stateElement) {
        const stateId = stateElement.id
        const stateCode = this.getStateCodeFromId(stateId)
        const value = this.getStateValue(stateCode)
        if (!value || value <= 0) {
          this.hoveredState = null
          this.tooltip.visible = false
          return
        }
        this.hoveredState = { code: stateCode, id: stateId }
        this.updateTooltipPosition(event)
        
        // Verifica se os dados estão em cache - não faz mais requests
        const cacheKey = `${stateCode}_${this.selectedMetric}`
        const isCached = this.stateDetailsCache[cacheKey]
        
        // Não mostra loading - os dados devem estar em cache via preloadStateDetails
        // Se não estiver em cache, simplesmente não mostra os detalhes
        if (!isCached) {
          // Se não está em cache, tenta carregar todos os estados (pode estar em processo)
          // Mas não mostra loading individual
          this.stateDetailsLoading = false
        } else {
          this.stateDetailsLoading = false
        }
      } else {
        this.hoveredState = null
      }
    },
    handleMapLeave(event) {
      if (this.isDragging) {
        this.endPan(event)
      } else {
        this.handleStateLeave(event)
      }
    },
    getStateCodeFromId(id) {
      const idToCode = {
        'tocantins': 'TO', 'bahia': 'BA', 'sergipe': 'SE', 'pernambuco': 'PE',
        'alagoas': 'AL', 'rio_grande_do_norte': 'RN', 'ceara': 'CE', 'piaui': 'PI',
        'maranhao': 'MA', 'amapa': 'AP', 'para': 'PA', 'roraima': 'RR',
        'amazonas': 'AM', 'acre': 'AC', 'rondonia': 'RO', 'mato_grosso': 'MT',
        'mato_grosso_do_sul': 'MS', 'goias': 'GO', 'parana': 'PR', 'santa_catarina': 'SC',
        'rio_grande_do_sul': 'RS', 'sao_paulo': 'SP', 'minas_gerais': 'MG',
        'rio_de_janeiro': 'RJ', 'espirito_santo': 'ES', 'distrito_federal': 'DF', 'paraiba': 'PB'
      }
      return idToCode[id] || id.toUpperCase().substring(0, 2)
    },
    ensureStateDetailsLoaded(code, showLoading = true) {
      // Não faz mais requests individuais - apenas verifica o cache
      // Todos os dados devem ser carregados via preloadStateDetails
      if (!code) return Promise.resolve()
      const value = this.getStateValue(code)
      if (!value || value <= 0) return Promise.resolve()
      
      const cacheKey = `${code}_all`
      // Verifica se já está em cache
      if (this.stateDetailsCache[cacheKey]) {
        return Promise.resolve()
      }
      
      // Se não está em cache, não mostra loading (os dados devem ser carregados via preloadStateDetails)
      // Retorna uma promise resolvida para manter compatibilidade
      return Promise.resolve()
    },
    async preloadStateDetails() {
      // Carrega os dados de TODOS os estados de uma vez usando a API (apenas uma chamada ao abrir)
      await this.$nextTick()
      
      const cacheKey = 'all_states_all_metrics'
      
      // Se já carregamos, nada a fazer
      if (this.stateDetailsCache[cacheKey] || this.allStatesLoaded) {
        return Promise.resolve()
      }
      
      // Evita requests duplicados
      if (this.isLoadingAllStates) {
        return Promise.resolve()
      }
      
      this.isLoadingAllStates = true
      
      // Monta a URL da API
      const urlParams = new URLSearchParams(window.location.search)
      const reportResultKey = urlParams.get('report_result_key') || 
                            urlParams.get('reportKey') ||
                            (typeof localStorage !== 'undefined' ? localStorage.getItem('report_result_key') : null) ||
                            ''
      
      // Endpoint fixo para buscar todos os estados
      const apiUrl = `reports/national-map/all-states-details/percentage/`
      const params = { arquivado: '0' }

      // Verifica se há filtros de data
      const hasDateFilter = this.dateFilter.period || 
                           this.dateFilter.dateStart || 
                           this.dateFilter.dateEnd ||
                           this.dateFilter.year || 
                           this.dateFilter.month || 
                           this.dateFilter.day || 
                           this.dateFilter.week
      
      // Só envia report_result_key se NÃO houver filtros de data
      if (!hasDateFilter && reportResultKey) {
        params.report_result_key = reportResultKey
      }
      
      // Adiciona timestamp para evitar cache
      params.t = new Date().getTime()
      
      // Adiciona parâmetros de data se houver filtro
      // Se for intervalo de datas (period 10), usa period=8 (backend) + date_start e date_end
      if (this.dateFilter.period === 10) {
        params.period = 8 // Backend usa period=8 para intervalo de datas customizado
        if (this.dateFilter.dateStart) {
          params.date_start = this.dateFilter.dateStart
        }
        if (this.dateFilter.dateEnd) {
          params.date_end = this.dateFilter.dateEnd
        }
      } else if (this.dateFilter.period) {
        // Se houver período selecionado (1-9), usa period
        params.period = this.dateFilter.period
      }
      
      // Suporta também: year, mes, dia, semana (para compatibilidade com filtros antigos)
      if (this.dateFilter.year && !this.dateFilter.period) {
        params.year = this.dateFilter.year
      }
      if (this.dateFilter.month && !this.dateFilter.period) {
        params.mes = this.dateFilter.month
      }
      if (this.dateFilter.day && !this.dateFilter.period) {
        params.dia = this.dateFilter.day
      }
      if (this.dateFilter.week && !this.dateFilter.period) {
        params.semana = this.dateFilter.week
      }
      
      try {
        // Faz o request para buscar todos os estados de uma vez
        const resp = await ApiService.get(apiUrl, { params })
        const data = resp && resp.data ? resp.data : resp
        
        if (data && data.states && Array.isArray(data.states)) {
          // Processa cada estado e armazena no cache
          data.states.forEach(stateData => {
            if (stateData && stateData.state) {
              const stateCode = stateData.state
              // Guardamos com um cacheKey genérico (não dependente de métrica)
              const stateCacheKey = `${stateCode}_all`
              
              // Normaliza os dados do estado
              const normalized = {
                top_shippings: Array.isArray(stateData.top_shippings) ? stateData.top_shippings.map(item => ({
                  name: item.name || item.label || '',
                  quantity: Number(item.count || item.quantity || item.total || 0)
                })) : [],
                top_products: Array.isArray(stateData.top_products) ? stateData.top_products.map(item => ({
                  name: item.name || item.label || item.product_name || 'Produto',
                  quantity: Number(item.count || item.quantity || item.total || 0)
                })) : [],
                top_cities: Array.isArray(stateData.top_cities) ? stateData.top_cities.map(item => ({
                  name: item.name || item.city || 'Cidade',
                  quantity: Number(item.count || item.quantity || item.total || 0)
                })) : [],
                customer_profile: stateData.customer_profile || null
              }
              
              // Armazena no cache
              this.$set(this.stateDetailsCache, stateCacheKey, normalized)
            }
          })
          
          // Marca como carregado
          this.$set(this.stateDetailsCache, cacheKey, true)
          this.allStatesLoaded = true
        }
        // Reseta a flag de carregamento
        this.isLoadingAllStates = false
        return Promise.resolve()
      } catch (error) {
        // Ignora erros silenciosamente
        console.warn('Erro ao carregar todos os estados de uma vez:', error)
        // Reseta a flag de carregamento mesmo em caso de erro
        this.isLoadingAllStates = false
        return Promise.resolve()
      }
    },
    handleStateClick(state) {
      if (this.clickFunction === '1') {
        this.exportStateData(state)
      } else {
        this.viewStateData(state)
      }
    },
    handleStateHover(state) {
      if (this.showTooltip) {
        this.hoveredState = state
      }
    },
    handleStateLeave() {
      this.hoveredState = null
      this.tooltip.visible = false
    },
    exportStateData() {
      // Método para exportar dados do estado
    },
    viewStateData() {
      // Método para visualizar dados do estado
    },
    togglePanel(panelId) {
      const panelKeyMap = {
        'filters-panel': 'filters',
        'regions-panel': 'regions',
        'customer-panel': 'customer',
        'top-ten-shipping-panel': 'shipping',
        'top-ten-products-panel': 'products',
        'top-ten-city-panel': 'cities',
        'top-ten-states-panel': 'states',
        'metrics-filters-panel': 'metricsFilters'
      }
      
      const panelKey = panelKeyMap[panelId] || panelId.replace('-panel', '')
      if (this.panelsVisible.hasOwnProperty(panelKey)) {
        this.panelsVisible[panelKey] = !this.panelsVisible[panelKey]
      }
    },
    zoomIn() {
      if (this.zoomLevel < this.maxZoom) {
        this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom)
        this.updateViewBox()
      }
    },
    zoomOut() {
      if (this.zoomLevel > this.minZoom) {
        this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom)
        this.updateViewBox()
      }
    },
    updateViewBox() {
      const newWidth = this.defaultViewBox.width / this.zoomLevel
      const newHeight = this.defaultViewBox.height / this.zoomLevel
      
      const currentCenterX = this.viewBoxValues.x + this.viewBoxValues.width / 2
      const currentCenterY = this.viewBoxValues.y + this.viewBoxValues.height / 2
      
      const newX = currentCenterX - newWidth / 2
      const newY = currentCenterY - newHeight / 2
      
      const minX = 0
      const minY = 0
      const maxX = this.defaultViewBox.width - newWidth
      const maxY = this.defaultViewBox.height - newHeight
      
      this.viewBoxValues = {
        x: Math.max(minX, Math.min(maxX, newX)),
        y: Math.max(minY, Math.min(maxY, newY)),
        width: newWidth,
        height: newHeight
      }
      
      this.currentViewBox = `${this.viewBoxValues.x} ${this.viewBoxValues.y} ${this.viewBoxValues.width} ${this.viewBoxValues.height}`
    },
    startPan(event) {
      // Permite arrastar o mapa de qualquer lugar, inclusive sobre os estados
      // Verifica se é um clique simples (não arrasto)
      // Se o mouse não se mover dentro de 200ms, consideramos como clique
      this.panStart = {
        x: event.clientX,
        y: event.clientY,
        time: Date.now()
      }
      
      // Sempre permite iniciar o arrasto
      this.isDragging = true
      
      // Esconder tooltip ao iniciar pan
      this.hoveredState = null
      
      event.preventDefault()
      event.stopPropagation()
    },
    handlePan(event) {
      if (!this.isDragging) return
      
      const deltaX = this.panStart.x - event.clientX
      const deltaY = this.panStart.y - event.clientY
      
      // Verifica se houve movimento significativo (mais de 5 pixels)
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      if (distance > 5) {
        this.wasDragging = true
      }
      
      const svgElement = document.getElementById('svg-map')
      if (!svgElement) return
      
      const svgRect = svgElement.getBoundingClientRect()
      const scaleX = this.viewBoxValues.width / svgRect.width
      const scaleY = this.viewBoxValues.height / svgRect.height
      
      const newX = this.viewBoxValues.x + (deltaX * scaleX)
      const newY = this.viewBoxValues.y + (deltaY * scaleY)
      
      const minX = 0
      const minY = 0
      const maxX = this.defaultViewBox.width - this.viewBoxValues.width
      const maxY = this.defaultViewBox.height - this.viewBoxValues.height
      
      this.viewBoxValues.x = Math.max(minX, Math.min(maxX, newX))
      this.viewBoxValues.y = Math.max(minY, Math.min(maxY, newY))
      
      this.currentViewBox = `${this.viewBoxValues.x} ${this.viewBoxValues.y} ${this.viewBoxValues.width} ${this.viewBoxValues.height}`
      
      this.panStart = {
        x: event.clientX,
        y: event.clientY,
        time: this.panStart.time || Date.now()
      }
      
      event.preventDefault()
      event.stopPropagation()
    },
    endPan() {
      if (this.isDragging) {
        this.isDragging = false
        // Reseta wasDragging após um pequeno delay para permitir que o clique seja processado
        setTimeout(() => {
          this.wasDragging = false
        }, 100)
      }
    },
    truncateText(text, maxLength) {
      if (!text) return ''
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
    },
    getMonthName(month) {
      const months = [
        '', 'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
      ]
      return months[month] || month
    },
    getDaysInMonth(year, month) {
      return new Date(Number(year), Number(month), 0).getDate()
    },
    handleDatePeriodClick(option, event) {
      if (event) {
        event.stopPropagation()
      }
      
      // Se for intervalo de datas, abre o DatePicker
      if (option.value === 10) {
        this.showDatePicker = true
        this.dateDropdownOpen = false
        document.removeEventListener('click', this.handleClickOutside)
        return
      }
      
      // Define o período selecionado
      this.selectedDatePeriod = option.value
      this.dateFilter.period = option.period
      
      // Limpa outros filtros
      this.dateFilter.year = ''
      this.dateFilter.month = ''
      this.dateFilter.day = ''
      this.dateFilter.week = ''
      this.dateFilter.dateStart = ''
      this.dateFilter.dateEnd = ''
      this.showDatePicker = false
      
      // Aplica o filtro
      this.applyDateFilter()
      
      // Fecha o dropdown
      this.dateDropdownOpen = false
      document.removeEventListener('click', this.handleClickOutside)
    },
    handleDateRangeSelected(date) {
      this.showDatePicker = false
      
      if (date && date.length > 0) {
        this.selectedDatePeriod = 10
        this.dateFilter.period = 10
        this.dateFilter.dateStart = date.length >= 1 ? date[0] : ''
        this.dateFilter.dateEnd = date.length >= 2 ? date[1] : date[0]
        
        // Limpa outros filtros
        this.dateFilter.year = ''
        this.dateFilter.month = ''
        this.dateFilter.day = ''
        this.dateFilter.week = ''
        
        // Aplica o filtro
        this.applyDateFilter()
      }
    },
    applyDateFilter() {
      // Limpa o cache para recarregar os dados com o novo filtro
      this.stateDetailsCache = {}
      this.allStatesLoaded = false
      
      // Recarrega os dados de todos os estados com o novo filtro de data
      this.preloadStateDetails()
      
      // Aplica o filtro de data recarregando a métrica atual
      const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
      if (selectedMetricObj && selectedMetricObj.endpoint) {
        this.applyMetricFilters(selectedMetricObj.endpoint)
      }
    },
    clearDateFilter() {
      this.selectedDatePeriod = null
      this.dateFilter = {
        period: null,
        year: '',
        month: '',
        day: '',
        week: '',
        dateStart: '',
        dateEnd: ''
      }
      this.showDatePicker = false
      
      // Limpa o cache para recarregar os dados sem filtro
      this.stateDetailsCache = {}
      this.allStatesLoaded = false
      
      // Recarrega os dados de todos os estados sem filtro de data
      this.preloadStateDetails()
      
      // Recarrega a métrica atual sem filtros de data
      const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
      if (selectedMetricObj && selectedMetricObj.endpoint) {
        this.applyMetricFilters(selectedMetricObj.endpoint)
      }
      
      // Fecha o dropdown após limpar
      this.dateDropdownOpen = false
      document.removeEventListener('click', this.handleClickOutside)
    },
    formatDate(date) {
      if (!date) return ''
      return date.split('-').reverse().join('/')
    },
    createExternalLabel(stateCode, value, stateElement) {
      const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
      if (!stateElement || !statesWithExternalLabels.includes(stateCode)) return
      
      const svgElement = document.getElementById('svg-map')
      if (!svgElement) return
      
      const currentLayer = svgElement.querySelector('g.currentLayer')
      if (!currentLayer) return
      
      const codeLower = stateCode.toLowerCase()
      const labelId = `external-label-${codeLower}`
      const lineId = `label-line-${codeLower}`
      
      let labelGroup = currentLayer.querySelector(`g#label-group-${codeLower}`)
      if (!labelGroup) {
        labelGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g')
        labelGroup.setAttribute('id', `label-group-${codeLower}`)
        labelGroup.classList.add('external-label-group')
        labelGroup.style.pointerEvents = 'none'
        currentLayer.appendChild(labelGroup)
      }
      
      setTimeout(() => {
        try {
          const bbox = stateElement.getBBox()
          if (!bbox || bbox.width === 0 || bbox.height === 0) return
          
          const stateCenterX = bbox.x + bbox.width / 2
          const stateCenterY = bbox.y + bbox.height / 2
          
          const mapWidth = 1022
          let labelX, labelY
          let lineX1, lineY1, lineX2, lineY2
          
          if (stateCenterX > mapWidth / 2) {
            labelX = bbox.x + bbox.width + 25
            labelY = stateCenterY
            lineX1 = bbox.x + bbox.width
            lineY1 = stateCenterY
            lineX2 = labelX - 5
            lineY2 = labelY
          } else {
            labelX = bbox.x - 25
            labelY = stateCenterY
            lineX1 = bbox.x
            lineY1 = stateCenterY
            lineX2 = labelX + 5
            lineY2 = labelY
          }
          
          let lineElement = labelGroup.querySelector(`line#${lineId}`)
          if (!lineElement) {
            lineElement = document.createElementNS('http://www.w3.org/2000/svg', 'line')
            lineElement.setAttribute('id', lineId)
            labelGroup.insertBefore(lineElement, labelGroup.firstChild)
          }
          lineElement.setAttribute('x1', lineX1.toString())
          lineElement.setAttribute('y1', lineY1.toString())
          lineElement.setAttribute('x2', lineX2.toString())
          lineElement.setAttribute('y2', lineY2.toString())
          lineElement.setAttribute('stroke', '#666')
          lineElement.setAttribute('stroke-width', '1')
          lineElement.style.display = 'block'
          lineElement.style.pointerEvents = 'none'
          
          let textElement = labelGroup.querySelector(`text#${labelId}`)
          if (!textElement) {
            textElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
            textElement.setAttribute('id', labelId)
            labelGroup.appendChild(textElement)
          }
          
          textElement.setAttribute('x', labelX.toString())
          textElement.setAttribute('y', labelY.toString())
          textElement.setAttribute('fill', '#000000')
          textElement.setAttribute('font-size', '12px')
          textElement.setAttribute('font-weight', 'bold')
          textElement.setAttribute('text-anchor', stateCenterX > mapWidth / 2 ? 'start' : 'end')
          textElement.setAttribute('dominant-baseline', 'middle')
          textElement.style.fontSize = '12px !important'
          textElement.style.setProperty('font-size', '12px', 'important')
          textElement.textContent = value || ''
          textElement.style.display = 'block'
          textElement.style.pointerEvents = 'none'
        } catch (e) {
          // Erro silencioso ao remover label externo
        }
      }, 50)
    },
    removeExternalLabel(stateCode) {
      const svgElement = document.getElementById('svg-map')
      if (!svgElement) return
      
      const currentLayer = svgElement.querySelector('g.currentLayer')
      if (!currentLayer) return
      
      const codeLower = stateCode.toLowerCase()
      const labelGroup = currentLayer.querySelector(`g#label-group-${codeLower}`)
      if (labelGroup) {
        labelGroup.remove()
      }
    },
    getRegionStates(regionName) {
      const regionMap = {
        'norte': ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO'],
        'nordeste': ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE'],
        'centro-oeste': ['DF', 'GO', 'MS', 'MT'],
        'centro oeste': ['DF', 'GO', 'MS', 'MT'],
        'sudeste': ['ES', 'MG', 'RJ', 'SP'],
        'sul': ['PR', 'RS', 'SC']
      }
      const normalizedName = regionName
        .toLowerCase()
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
      
      if (regionMap[normalizedName]) {
        return regionMap[normalizedName]
      }
      
      for (const [key, states] of Object.entries(regionMap)) {
        if (normalizedName.includes(key) || key.includes(normalizedName)) {
          return states
        }
      }
      
      return []
    },
    isRegionSelected(region) {
      return this.selectedRegions.some(r => r.name === region.name)
    },
    toggleRegion(region) {
      const index = this.selectedRegions.findIndex(r => r.name === region.name)
      if (index >= 0) {
        this.selectedRegions.splice(index, 1)
      } else {
        this.selectedRegions.push(region)
      }
      this.$nextTick(() => {
        this.applyStateStyles()
        setTimeout(() => {
          this.applyRegionFilter()
        }, 100)
      })
    },
    removeRegionFilter(region) {
      const index = this.selectedRegions.findIndex(r => r.name === region.name)
      if (index >= 0) {
        this.selectedRegions.splice(index, 1)
        this.$nextTick(() => {
          this.applyStateStyles()
          setTimeout(() => {
            this.applyRegionFilter()
          }, 100)
        })
      }
    },
    selectMetric(metric) {
      this.selectedMetric = metric.value
      this.metricsDropdownOpen = false
      document.removeEventListener('click', this.handleClickOutside)
      this.applyMetricFilters(metric.endpoint)
    },
    handleMetricsButtonClick(event) {
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      
      const willOpen = !this.metricsDropdownOpen
      this.metricsDropdownOpen = willOpen
      
      if (willOpen) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.metricsDropdownOpen) {
              document.addEventListener('click', this.handleClickOutside)
            }
          }, 100)
        })
      } else {
        document.removeEventListener('click', this.handleClickOutside)
      }
    },
    handleMenuClick(event) {
      event.stopPropagation()
    },
    handleOptionClick(metric, event) {
      if (event) {
        event.stopPropagation()
      }
      this.selectMetric(metric)
    },
    handleClickOutside(event) {
      // Verifica se deve fechar o dropdown de métricas
      if (this.metricsDropdownOpen) {
        const dropdown = this.$refs.metricsDropdown
        if (dropdown) {
          const clickedInside = dropdown.contains(event.target)
          if (!clickedInside) {
            this.metricsDropdownOpen = false
            document.removeEventListener('click', this.handleClickOutside)
          }
        }
      }
      
      // Verifica se deve fechar o dropdown de data
      if (this.dateDropdownOpen) {
        const dateDropdown = this.$refs.dateDropdown
        if (dateDropdown) {
          const clickedInside = dateDropdown.contains(event.target)
          if (!clickedInside) {
            this.dateDropdownOpen = false
            document.removeEventListener('click', this.handleClickOutside)
          }
        }
      }
    },
    handleDateButtonClick(event) {
      if (event) {
        event.stopPropagation()
        event.preventDefault()
      }
      
      const willOpen = !this.dateDropdownOpen
      this.dateDropdownOpen = willOpen
      
      if (willOpen) {
        this.$nextTick(() => {
          setTimeout(() => {
            if (this.dateDropdownOpen) {
              document.addEventListener('click', this.handleClickOutside)
            }
          }, 100)
        })
      } else {
        document.removeEventListener('click', this.handleClickOutside)
      }
    },
    handleDateMenuClick(event) {
      event.stopPropagation()
    },
    applyMetricFilters(endpoint) {
      const params = new URLSearchParams()
      
      params.append('arquivado', '0')
      
      const urlParams = new URLSearchParams(window.location.search)
      const reportResultKey = urlParams.get('report_result_key') || 
                            urlParams.get('reportKey') ||
                            this.$store.getters.report_result_key || 
                            this.$store.state.report_result_key ||
                            (typeof localStorage !== 'undefined' ? localStorage.getItem('report_result_key') : null) ||
                            ''
      
      // Verifica se há filtros de data
      const hasDateFilter = this.dateFilter.period || 
                           this.dateFilter.dateStart || 
                           this.dateFilter.dateEnd ||
                           this.dateFilter.year || 
                           this.dateFilter.month || 
                           this.dateFilter.day || 
                           this.dateFilter.week
      
      // Só envia report_result_key se NÃO houver filtros de data
      if (!hasDateFilter && reportResultKey) {
        params.append('report_result_key', reportResultKey)
      }
      
      // Adiciona timestamp para evitar cache
      params.append('t', new Date().getTime())
      
      // Adiciona parâmetros de data do componente
      // Se for intervalo de datas (period 10), usa period=8 (backend) + date_start e date_end
      if (this.dateFilter.period === 10) {
        params.append('period', 8) // Backend usa period=8 para intervalo de datas customizado
        if (this.dateFilter.dateStart) {
          params.append('date_start', this.dateFilter.dateStart)
        }
        if (this.dateFilter.dateEnd) {
          params.append('date_end', this.dateFilter.dateEnd)
        }
      } else if (this.dateFilter.period) {
        // Se houver período selecionado (1-9), usa period
        params.append('period', this.dateFilter.period)
      }
      
      // Suporta também: year, mes, dia, semana (para compatibilidade com filtros antigos)
      if (this.dateFilter.year && !this.dateFilter.period) {
        params.append('year', this.dateFilter.year)
      }
      if (this.dateFilter.month && !this.dateFilter.period) {
        params.append('mes', this.dateFilter.month)
      }
      if (this.dateFilter.day && !this.dateFilter.period) {
        params.append('dia', this.dateFilter.day)
      }
      if (this.dateFilter.week && !this.dateFilter.period) {
        params.append('semana', this.dateFilter.week)
      }
      
      // Também verifica se há parâmetros de data na URL (para compatibilidade)
      if (urlParams.get('period') && !this.dateFilter.period) {
        params.append('period', urlParams.get('period'))
      }
      
      const queryString = params.toString()
      const url = `reports/national-map/${endpoint}/?${queryString}`

      this.$store.commit('updateSalesMapContext', { 
        title: '',
        total_orders: 0,
        applied_filters: {},
        regions: [],
        states: {},
        customer_profile: {},
        top_shippings: []
      })
      
      this.$store.commit('requestCommonContext', {
        'url': url,
        'period': null
      })
      
      // O watcher de selectedMetric vai cuidar de carregar os dados e atualizar os estilos
      // Aqui apenas aguardamos o store processar a requisição
      this.$nextTick(() => {
        // Aguarda o store processar a requisição antes de atualizar os estilos
        // O watcher selectedMetric vai chamar preloadStateDetails
        setTimeout(() => {
          this.applyStateStyles()
        }, 800)
      })
    },
    setSvgTextFontSize() {
      const svgRoot = document.getElementById('svg-map')
      if (!svgRoot) return
      const textNodes = svgRoot.querySelectorAll('text')
      textNodes.forEach(node => {
        node.setAttribute('font-size', '7px')
        node.style.fontSize = '7px'
      })
    },
    updateTooltipPosition(event) {
      const container = this.$el.querySelector('.map-body')
      if (!container) return
      const rect = container.getBoundingClientRect()
      const offset = 12
      const tooltipWidth = 300
      const tooltipHeight = (this.showTooltip && this.stateCustomerProfile) ? 450 : 260
      let x = event.clientX - rect.left + offset
      let y = event.clientY - rect.top + offset
      const maxX = rect.width - tooltipWidth - offset
      const maxY = rect.height - tooltipHeight - offset
      if (x > maxX) x = Math.max(0, event.clientX - rect.left - tooltipWidth - offset)
      if (y > maxY) y = Math.max(0, event.clientY - rect.top - tooltipHeight - offset)
      this.tooltip.x = Math.max(0, x)
      this.tooltip.y = Math.max(0, y)
      this.tooltip.visible = true
    },
    getStateNameFromCode(code) {
      const map = {
        'AC':'Acre','AL':'Alagoas','AP':'Amapá','AM':'Amazonas','BA':'Bahia','CE':'Ceará',
        'DF':'Distrito Federal','ES':'Espírito Santo','GO':'Goiás','MA':'Maranhão','MT':'Mato Grosso',
        'MS':'Mato Grosso do Sul','MG':'Minas Gerais','PA':'Pará','PB':'Paraíba','PR':'Paraná',
        'PE':'Pernambuco','PI':'Piauí','RJ':'Rio de Janeiro','RN':'Rio Grande do Norte','RS':'Rio Grande do Sul',
        'RO':'Rondônia','RR':'Roraima','SC':'Santa Catarina','SP':'São Paulo','SE':'Sergipe','TO':'Tocantins'
      }
      return map[code] || code
    },
    applyRegionFilter() {
      this.$nextTick(() => {
        setTimeout(() => {
          const svgElement = document.getElementById('svg-map')
          if (!svgElement) return
          
          if (this.selectedRegions.length === 0) {
            this.zoomLevel = 1
            this.viewBoxValues = { x: 0, y: 0, width: 1022, height: 840 }
            this.currentViewBox = '0 0 1022 840'
            svgElement.setAttribute('viewBox', this.currentViewBox)
            this.isDragging = false
            const allAnchors = svgElement.querySelectorAll('a[id]')
            allAnchors.forEach(anchor => {
              anchor.style.display = 'block'
            })
            return
          }
          
          const selectedStatesSet = new Set()
          this.selectedRegions.forEach(region => {
            const regionStates = this.getRegionStates(region.name)
            regionStates.forEach(state => selectedStatesSet.add(state))
          })
          const selectedStates = Array.from(selectedStatesSet)
          
          const stateIdMap = {
            'ac': 'acre', 'al': 'alagoas', 'ap': 'amapa', 'am': 'amazonas',
            'ba': 'bahia', 'ce': 'ceara', 'df': 'distrito_federal', 'es': 'espirito_santo',
            'go': 'goias', 'ma': 'maranhao', 'mt': 'mato_grosso', 'ms': 'mato_grosso_do_sul',
            'mg': 'minas_gerais', 'pa': 'para', 'pb': 'paraiba', 'pr': 'parana',
            'pe': 'pernambuco', 'pi': 'piaui', 'rj': 'rio_de_janeiro', 'rn': 'rio_grande_do_norte',
            'rs': 'rio_grande_do_sul', 'ro': 'rondonia', 'rr': 'roraima', 'sc': 'santa_catarina',
            'sp': 'sao_paulo', 'se': 'sergipe', 'to': 'tocantins'
          }
          
          // Calcular bounding box dos estados selecionados
          let minX = Infinity
          let minY = Infinity
          let maxX = -Infinity
          let maxY = -Infinity
          let hasVisibleStates = false
          
          selectedStates.forEach(code => {
            const codeLower = code.toLowerCase()
            const stateId = stateIdMap[codeLower]
            
            let stateAnchor = null
            if (stateId) {
              stateAnchor = document.getElementById(stateId)
            }
            if (!stateAnchor) {
              const stateElement = document.getElementById(`svg_${codeLower}`)
              if (stateElement) {
                stateAnchor = stateElement.closest('a[id]')
              }
            }
            
            const stateElement = document.getElementById(`svg_${codeLower}`)
            if (stateElement && stateAnchor && stateAnchor.style.display !== 'none') {
              try {
                const bbox = stateElement.getBBox()
                if (bbox && bbox.width > 0 && bbox.height > 0) {
                  minX = Math.min(minX, bbox.x)
                  minY = Math.min(minY, bbox.y)
                  maxX = Math.max(maxX, bbox.x + bbox.width)
                  maxY = Math.max(maxY, bbox.y + bbox.height)
                  hasVisibleStates = true
                }
              } catch (e) {
                // Erro silencioso ao processar estado
              }
            }
          })
          
          if (hasVisibleStates) {
            const width = maxX - minX
            const height = maxY - minY
            const paddingX = Math.max(width * 0.15, 30)
            const paddingY = Math.max(height * 0.15, 30)
            
            const finalWidth = width + (paddingX * 2)
            const finalHeight = height + (paddingY * 2)
            const x = Math.max(0, minX - paddingX)
            const y = Math.max(0, minY - paddingY)
            
            this.viewBoxValues = { x, y, width: finalWidth, height: finalHeight }
            this.currentViewBox = `${x} ${y} ${finalWidth} ${finalHeight}`
            svgElement.setAttribute('viewBox', this.currentViewBox)
            this.zoomLevel = this.defaultViewBox.width / finalWidth
          }
        }, 300)
      })
    },
    applyStateStyles() {
      this.$nextTick(() => {
        setTimeout(() => {
          const allStates = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];
          
          const hasSelectedRegions = this.selectedRegions.length > 0
          const selectedStateCodes = []
          if (hasSelectedRegions) {
            this.selectedRegions.forEach(region => {
              const regionStates = this.getRegionStates(region.name)
              selectedStateCodes.push(...regionStates)
            })
          }
          
          allStates.forEach(code => {
            const originalValue = this.stateValues[code]
            const numericValue = typeof originalValue === 'number' 
              ? originalValue 
              : (originalValue ? parseFloat(String(originalValue).replace(/%/g, '').replace(/R\$/g, '').replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '.')) || 0 : 0)
            
            const codeLower = code.toLowerCase()
            const element = document.getElementById(`svg_${codeLower}`)
            const textElement = document.getElementById(`text_${codeLower}`)
            
            // Mapear códigos de estado para IDs dos elementos <a> no SVG
            const stateIdMap = {
              'ac': 'acre', 'al': 'alagoas', 'ap': 'amapa', 'am': 'amazonas',
              'ba': 'bahia', 'ce': 'ceara', 'df': 'distrito_federal', 'es': 'espirito_santo',
              'go': 'goias', 'ma': 'maranhao', 'mt': 'mato_grosso', 'ms': 'mato_grosso_do_sul',
              'mg': 'minas_gerais', 'pa': 'para', 'pb': 'paraiba', 'pr': 'parana',
              'pe': 'pernambuco', 'pi': 'piaui', 'rj': 'rio_de_janeiro', 'rn': 'rio_grande_do_norte',
              'rs': 'rio_grande_do_sul', 'ro': 'rondonia', 'rr': 'roraima', 'sc': 'santa_catarina',
              'sp': 'sao_paulo', 'se': 'sergipe', 'to': 'tocantins'
            }
            
            // Buscar o elemento <a> que contém o estado
            let stateAnchor = null
            const stateId = stateIdMap[codeLower]
            if (stateId) {
              stateAnchor = document.getElementById(stateId)
            }
            // Se não encontrou pelo mapeamento, tentar pelo elemento path
            if (!stateAnchor && element) {
              stateAnchor = element.closest('a[id]')
            }
            
            // Buscar círculo dentro do anchor
            const circleElement = stateAnchor ? 
              stateAnchor.querySelector(`.circle`) : 
              (element ? element.parentElement.querySelector(`.circle`) : null)
            
            // Verificar se o estado pertence a uma região selecionada
            const isInSelectedRegion = hasSelectedRegions && selectedStateCodes.includes(code)
            
            if (element) {
              element.classList.remove('northeast-color', 'north-color', 'middlewestern-color', 'southeast-color', 'south-color', 'region-highlighted')
              
              if (numericValue > 0) {
                const colorClass = this.getStateColorClass(code)
                const colorValue = this.getStateColorValue(code)
                
                if (colorClass) {
                  element.classList.add(colorClass)
                }
                element.setAttribute('fill', colorValue)
                element.style.fill = colorValue
                
                if (isInSelectedRegion) {
                  element.classList.add('region-highlighted')
                  element.setAttribute('stroke', '#FFD700')
                  element.setAttribute('stroke-width', '3')
                  element.style.stroke = '#FFD700'
                  element.style.strokeWidth = '3'
                } else {
                  element.setAttribute('stroke', '#FFFFFF')
                  element.setAttribute('stroke-width', '1.0404')
                  element.style.stroke = '#FFFFFF'
                  element.style.strokeWidth = '1.0404'
                }
                
                if (circleElement) {
                  circleElement.style.display = 'block'
                }
              } else {
                element.setAttribute('fill', '#b1b1b1')
                element.style.fill = '#b1b1b1'
                
                if (isInSelectedRegion) {
                  element.classList.add('region-highlighted')
                  element.setAttribute('stroke', '#FFD700')
                  element.setAttribute('stroke-width', '3')
                  element.style.stroke = '#FFD700'
                  element.style.strokeWidth = '3'
                } else {
                  element.setAttribute('stroke', '#FFFFFF')
                  element.setAttribute('stroke-width', '1.0404')
                  element.style.stroke = '#FFFFFF'
                  element.style.strokeWidth = '1.0404'
                }
                
                if (circleElement) {
                  circleElement.style.display = 'none'
                }
              }
            }
            
            if (textElement) {
              // Esconder textos dos estados que têm label externo (valores ficam fora com linha)
              const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
              if (statesWithExternalLabels.includes(code)) {
                textElement.style.display = 'none'
              } else if (numericValue > 0 && originalValue !== null && originalValue !== undefined && originalValue !== '') {
                let formattedValue = ''
                if (typeof originalValue === 'string') {
                  formattedValue = originalValue
                } else {
                  const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
                  const endpoint = selectedMetricObj ? selectedMetricObj.endpoint : 'percentage'
                  
                  if (endpoint === 'percentage') {
                    formattedValue = String(originalValue) + '%'
                  } else if (endpoint === 'total_sales' || endpoint === 'total_shipping_cost' || endpoint === 'sales_ticket' || endpoint === 'shipping_cost_ticket') {
                    formattedValue = 'R$ ' + originalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  } else {
                    formattedValue = originalValue.toLocaleString('pt-BR')
                  }
                }
                
                textElement.textContent = formattedValue
                textElement.setAttribute('fill', '#000000')
                textElement.style.fill = '#000000'
                textElement.style.display = 'block'
              } else {
                textElement.textContent = ''
                textElement.style.display = 'none'
                if (circleElement) {
                  circleElement.style.display = 'none'
                }
              }
            }
            
            if (stateAnchor) {
              if (hasSelectedRegions) {
                if (!isInSelectedRegion) {
                  stateAnchor.style.display = 'none'
                  if (textElement) {
                    textElement.style.display = 'none'
                  }
                  if (circleElement) {
                    circleElement.style.display = 'none'
                  }
                } else {
                  stateAnchor.style.display = 'block'
                  if (textElement) {
                    // Esconder textos dos estados que têm label externo (valores ficam fora com linha)
                    const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
                    if (statesWithExternalLabels.includes(code)) {
                      textElement.style.display = 'none'
                    } else if (numericValue > 0 && originalValue !== null && originalValue !== undefined && originalValue !== '') {
                      let formattedValue = ''
                      if (typeof originalValue === 'string') {
                        formattedValue = originalValue
                      } else {
                        const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
                        const endpoint = selectedMetricObj ? selectedMetricObj.endpoint : 'percentage'
                        
                        if (endpoint === 'percentage') {
                          formattedValue = String(originalValue) + '%'
                        } else if (endpoint === 'total_sales' || endpoint === 'total_shipping_cost' || endpoint === 'sales_ticket' || endpoint === 'shipping_cost_ticket') {
                          formattedValue = 'R$ ' + originalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        } else {
                          formattedValue = originalValue.toLocaleString('pt-BR')
                        }
                      }
                      textElement.textContent = formattedValue
                      textElement.setAttribute('fill', '#000000')
                      textElement.style.fill = '#000000'
                      textElement.style.display = 'block'
                    } else {
                      textElement.style.display = 'none'
                    }
                  }
                  if (circleElement) {
                    // Esconder círculos dos estados que têm label externo (círculos já foram removidos do SVG)
                    const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
                    if (statesWithExternalLabels.includes(code)) {
                      circleElement.style.display = 'none'
                    } else if (numericValue > 0 && originalValue !== null && originalValue !== undefined && originalValue !== '') {
                      circleElement.style.display = 'block'
                    } else {
                      circleElement.style.display = 'none'
                    }
                  }
                }
              } else {
                // Se não há regiões selecionadas, mostrar todos
                stateAnchor.style.display = 'block'
                // Restaurar visibilidade de texto e círculo conforme valor
                if (textElement) {
                  // Esconder textos dos estados que têm label externo (valores ficam fora com linha)
                  const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
                  if (statesWithExternalLabels.includes(code)) {
                    textElement.style.display = 'none'
                  } else if (numericValue > 0 && originalValue !== null && originalValue !== undefined && originalValue !== '') {
                    let formattedValue = ''
                    if (typeof originalValue === 'string') {
                      formattedValue = originalValue
                    } else {
                      const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
                      const endpoint = selectedMetricObj ? selectedMetricObj.endpoint : 'percentage'
                      
                      if (endpoint === 'percentage') {
                        formattedValue = String(originalValue) + '%'
                      } else if (endpoint === 'total_sales' || endpoint === 'total_shipping_cost' || endpoint === 'sales_ticket' || endpoint === 'shipping_cost_ticket') {
                        formattedValue = 'R$ ' + originalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      } else {
                        formattedValue = originalValue.toLocaleString('pt-BR')
                      }
                    }
                    textElement.textContent = formattedValue
                    textElement.setAttribute('fill', '#000000')
                    textElement.style.fill = '#000000'
                    textElement.style.display = 'block'
                  } else {
                    textElement.style.display = 'none'
                  }
                }
                if (circleElement) {
                  // Esconder círculos dos estados que têm label externo (círculos já foram removidos do SVG)
                  const statesWithExternalLabels = ['RN', 'SE', 'AL', 'PB', 'PE', 'ES', 'RJ', 'DF']
                  if (statesWithExternalLabels.includes(code)) {
                    circleElement.style.display = 'none'
                  } else if (numericValue > 0 && originalValue !== null && originalValue !== undefined && originalValue !== '') {
                    circleElement.style.display = 'block'
                  } else {
                    circleElement.style.display = 'none'
                  }
                }
              }
            }
          })
          // Remover todos os retângulos de background de texto
          const allBackgroundRects = document.querySelectorAll('#svg-map rect.text-background')
          allBackgroundRects.forEach(rect => {
            rect.remove()
          })
          
          // Após atualizar conteúdos, garantir tamanho da fonte dos textos do SVG
          this.setSvgTextFontSize()
          
          // Criar labels externos com linhas para estados específicos
          const statesWithExternalLabels = [
            { code: 'RN', elementId: 'svg_rn', anchorId: 'rio_grande_do_norte' },
            { code: 'SE', elementId: 'svg_se', anchorId: 'sergipe' },
            { code: 'AL', elementId: 'svg_al', anchorId: 'alagoas' },
            { code: 'PB', elementId: 'svg_pb', anchorId: 'paraiba' },
            { code: 'PE', elementId: 'svg_pe', anchorId: 'pernambuco' },
            { code: 'ES', elementId: 'svg_es', anchorId: 'espirito_santo' },
            { code: 'RJ', elementId: 'svg_rj', anchorId: 'rio_de_janeiro' },
            { code: 'DF', elementId: 'svg_df', anchorId: 'distrito_federal' }
          ]
          
          statesWithExternalLabels.forEach(state => {
            const stateElement = document.getElementById(state.elementId)
            const stateAnchor = document.getElementById(state.anchorId)
            if (stateElement && stateAnchor) {
              const stateValue = this.stateValues[state.code]
              const numericValue = typeof stateValue === 'number' 
                ? stateValue 
                : (stateValue ? parseFloat(String(stateValue).replace(/%/g, '').replace(/R\$/g, '').replace(/\s/g, '').replace(/\./g, '').replace(/,/g, '.')) || 0 : 0)
              
              if (stateValue !== null && stateValue !== undefined && stateValue !== '' && numericValue > 0) {
                let formattedValue = ''
                if (typeof stateValue === 'string') {
                  formattedValue = stateValue
                } else {
                  const selectedMetricObj = this.availableMetrics.find(m => m.value === this.selectedMetric)
                  const endpoint = selectedMetricObj ? selectedMetricObj.endpoint : 'percentage'
                  
                  if (endpoint === 'percentage') {
                    formattedValue = String(stateValue) + '%'
                  } else if (endpoint === 'total_sales' || endpoint === 'total_shipping_cost' || endpoint === 'sales_ticket' || endpoint === 'shipping_cost_ticket') {
                    formattedValue = 'R$ ' + stateValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  } else {
                    formattedValue = stateValue.toLocaleString('pt-BR')
                  }
                }
                this.createExternalLabel(state.code, formattedValue, stateElement)
              } else {
                this.removeExternalLabel(state.code)
              }
            }
          })
          
          // Colorir e aumentar os círculos (bolas) conforme a região do estado
          const circleNodes = document.querySelectorAll('#svg-map .circle')
          circleNodes.forEach(circle => {
            const anchor = circle.closest('a[id]')
            if (!anchor) return
            const stateId = anchor.id
            const stateCode = this.getStateCodeFromId(stateId)
            
            
            const colorValue = this.getStateColorValue(stateCode)
            
            // cor da bola conforme região
            circle.setAttribute('fill', colorValue)
            circle.style.fill = colorValue
            circle.setAttribute('stroke', '#ffffff')
            circle.setAttribute('stroke-width', '1')
            
            // aumentar a bola para caber os números
            circle.style.transformBox = 'fill-box'
            circle.style.transformOrigin = 'center'
            circle.style.transform = 'scale(1.3)'
          })
        }, 300)
      })
    },
    getStateColorValue(code) {
      // Retornar o valor hexadecimal da cor baseado na região
      const northeast = ['AL', 'BA', 'CE', 'MA', 'PB', 'PE', 'PI', 'RN', 'SE']
      const north = ['AC', 'AP', 'AM', 'PA', 'RO', 'RR', 'TO']
      const midwest = ['DF', 'GO', 'MS', 'MT']
      const southeast = ['ES', 'MG', 'RJ', 'SP']
      const south = ['PR', 'RS', 'SC']
      
      if (northeast.includes(code)) return '#f56651'
      if (north.includes(code)) return '#51c35f'
      if (midwest.includes(code)) return '#0094d9'
      if (southeast.includes(code)) return '#e8b523'
      if (south.includes(code)) return '#ad55ea'
      
      return '#b1b1b1' // Cor padrão
    },
    protectSidebar() {
      // Força o sidebar a sempre estar visível
      try {
        const sidebar = document.getElementById('sidebar')
        const sidebarCol = document.querySelector('.sidebar-col')
        
        if (sidebar) {
          // Remove qualquer display: none que possa ter sido aplicado
          const computedStyle = window.getComputedStyle(sidebar)
          if (sidebar.style.display === 'none' || computedStyle.display === 'none') {
            sidebar.style.display = 'block'
          }
          if (sidebar.style.visibility === 'hidden' || computedStyle.visibility === 'hidden') {
            sidebar.style.visibility = 'visible'
          }
          if (computedStyle.opacity === '0') {
            sidebar.style.opacity = '1'
          }
        }
        
        if (sidebarCol) {
          // Remove qualquer display: none que possa ter sido aplicado
          const computedStyle = window.getComputedStyle(sidebarCol)
          if (sidebarCol.style.display === 'none' || computedStyle.display === 'none') {
            sidebarCol.style.display = 'flex'
          }
          if (sidebarCol.style.visibility === 'hidden' || computedStyle.visibility === 'hidden') {
            sidebarCol.style.visibility = 'visible'
          }
        }
      } catch (e) {
        // Erro silencioso ao proteger sidebar
      }
    }
  },
  mounted() {
    this.applyDateFilter()
    
    this.$nextTick(() => {
      // Proteção: garante que o sidebar sempre fique visível
      if (typeof this.protectSidebar === 'function') {
        this.protectSidebar()
      }
      
      setTimeout(() => {
        this.applyStateStyles()
        // Protege novamente após aplicar estilos
        if (typeof this.protectSidebar === 'function') {
          this.protectSidebar()
        }
      }, 500)
      
      // applyDateFilter() já chama preloadStateDetails() no mount — nenhum preload adicional necessário
      
      // Observa mudanças no DOM para proteger o sidebar
      if (typeof MutationObserver !== 'undefined') {
        this.sidebarObserver = new MutationObserver(() => {
          if (typeof this.protectSidebar === 'function') {
            this.protectSidebar()
          }
        })
        
        // Inicia observação do sidebar após um delay
        setTimeout(() => {
          const sidebar = document.getElementById('sidebar')
          const sidebarCol = document.querySelector('.sidebar-col')
          if (sidebar && this.sidebarObserver) {
            this.sidebarObserver.observe(sidebar, {
              attributes: true,
              attributeFilter: ['style', 'class']
            })
          }
          if (sidebarCol && this.sidebarObserver) {
            this.sidebarObserver.observe(sidebarCol, {
              attributes: true,
              attributeFilter: ['style', 'class']
            })
          }
        }, 1000)
      }
    })
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    // Para a observação do sidebar quando o componente for destruído
    if (this.sidebarObserver) {
      this.sidebarObserver.disconnect()
      this.sidebarObserver = null
    }
  },
  watch: {
    stateValues: {
      handler(newVal, oldVal) {
        // Só aplica estilos e carrega dados se realmente houver valores
        if (!newVal || Object.keys(newVal).length === 0) {
          return
        }
        
        // Só processa se os valores realmente mudaram (evita processar na inicialização desnecessariamente)
        const hasChanged = !oldVal || JSON.stringify(newVal) !== JSON.stringify(oldVal)
        if (!hasChanged && oldVal) {
          return
        }
        
        // Se já está carregando (provavelmente por mudança de métrica), não faz nada
        // O watcher de selectedMetric já está cuidando disso
        if (this.isLoadingAllStates) {
          // Apenas atualiza os estilos quando o carregamento terminar
          return
        }
        
        this.$nextTick(() => {
          // Carrega os dados de todos os estados imediatamente quando os valores mudam
          // Mas só se não estiver carregando já (evita duplicação)
          if (!this.isLoadingAllStates) {
            this.preloadStateDetails().then(() => {
              // Após carregar os dados, atualiza os estilos
              this.$nextTick(() => {
                setTimeout(() => {
                  this.applyStateStyles()
                  // Protege o sidebar após aplicar estilos
                  if (typeof this.protectSidebar === 'function') {
                    this.protectSidebar()
                  }
                }, 100)
              })
            }).catch(() => {
              // Mesmo em caso de erro, tenta atualizar os estilos
              this.$nextTick(() => {
                setTimeout(() => {
                  this.applyStateStyles()
                  // Protege o sidebar após aplicar estilos
                  if (typeof this.protectSidebar === 'function') {
                    this.protectSidebar()
                  }
                }, 100)
              })
            })
          } else {
            // Se está carregando, apenas atualiza os estilos
            this.$nextTick(() => {
              setTimeout(() => {
                this.applyStateStyles()
                // Protege o sidebar após aplicar estilos
                if (typeof this.protectSidebar === 'function') {
                  this.protectSidebar()
                }
              }, 100)
            })
          }
        })
      },
      deep: true,
      immediate: false
    },
    selectedMetric(newVal, oldVal) {
      // Só processa se realmente mudou
      if (newVal === oldVal) return
      
      // Protege o sidebar quando a métrica muda
      this.$nextTick(() => {
        if (typeof this.protectSidebar === 'function') {
          this.protectSidebar()
        }
        // Aguarda um pouco para garantir que o applyMetricFilters terminou de atualizar o store
        // e então carrega TODOS os dados com a nova métrica (apenas 1 request)
        setTimeout(() => {
          // Recarrega os detalhes de todos os estados com a nova métrica
          // A flag isLoadingAllStates vai evitar requests duplicados
          this.preloadStateDetails().then(() => {
            // Após carregar os dados, atualiza os estilos
            this.$nextTick(() => {
              setTimeout(() => {
                this.applyStateStyles()
                // Protege o sidebar após aplicar estilos
                if (typeof this.protectSidebar === 'function') {
                  this.protectSidebar()
                }
              }, 200)
            })
          }).catch(() => {
            // Mesmo em caso de erro, tenta atualizar os estilos
            this.$nextTick(() => {
              setTimeout(() => {
                this.applyStateStyles()
                // Protege o sidebar após aplicar estilos
                if (typeof this.protectSidebar === 'function') {
                  this.protectSidebar()
                }
              }, 200)
            })
          })
        }, 300)
      })
    },
    sales_map_context_loaded(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          setTimeout(() => {
            this.applyStateStyles()
            this.preloadStateDetails()
          }, 100)
        })
      }
    }
  }
}
</script>

<style scoped>
.sales-map-container {
  position: relative;
  max-height: calc(100vh - 120px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
}

#svg-map path { 
  fill: #b1b1b1 !important; 
  transition: fill 0.3s ease;
}

#svg-map a { 
  text-decoration: none; 
}

#svg-map a:hover { 
  cursor: pointer; 
  text-decoration: none; 
}

#svg-map a:hover path { 
  fill: #5AB6DF !important; 
}

#svg-map a:hover .circle { 
  cursor: pointer; 
}

/* Cores por região */
.northeast-color { 
  fill: #f56651 !important; 
}

.northeast-color-circle { 
  fill: #f88674 !important; 
}

.north-color { 
  fill: #51c35f !important; 
}

.middlewestern-color { 
  fill: #0094d9 !important; 
}

.middlewestern-color-circle { 
  fill: #66ccff !important; 
}

.southeast-color { 
  fill: #e8b523 !important; 
}

.southeast-color-circle { 
  fill: #e9c050 !important; 
}

.south-color { 
  fill: #ad55ea !important; 
}

.map-controls-bar {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 12px 20px;
}

.controls-content {
  gap: 24px;
}

.control-group {
  gap: 8px;
}

.control-label {
  font-weight: 500;
  color: #666;
  white-space: nowrap;
  font-size: 13px;
}

.control-option {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: color 0.2s ease;
}

.control-option:hover {
  color: #333;
}

.control-option input[type="radio"],
.control-option input[type="checkbox"] {
  margin: 0;
  cursor: pointer;
  width: 14px;
  height: 14px;
}

.radio-group {
  gap: 16px;
}

.metrics-select-wrapper {
  position: relative;
  min-width: 280px;
  z-index: 1000;
}

.metrics-select-button {
  width: 100%;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  outline: none;
  user-select: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.metrics-select-button:hover {
  background: #f8f8f8;
  border-color: #d0d0d0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.12);
}

.metrics-select-button.is-open {
  background: #fff;
  color: #333;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.metrics-select-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.metrics-select-arrow {
  flex-shrink: 0;
  font-size: 10px;
  transition: transform 0.2s ease;
  display: inline-block;
}

.metrics-select-arrow.is-open {
  transform: rotate(180deg);
}

.metrics-select-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
}

.metrics-select-option {
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  user-select: none;
}

.metrics-select-option:last-child {
  border-bottom: none;
}

.metrics-select-option:hover {
  background: #f5f5f5;
  color: #333;
}

.metrics-select-option.is-selected {
  background: #f8f8f8;
  color: #333;
  font-weight: 500;
}

.date-select-wrapper {
  position: relative;
  min-width: 200px;
  z-index: 1000;
}

.date-select-button {
  width: 100%;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #333;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  outline: none;
  user-select: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.date-select-button:hover {
  background: #f8f8f8;
  border-color: #d0d0d0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.12);
}

.date-select-button.is-open {
  background: #fff;
  color: #333;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.date-select-text {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.date-select-arrow {
  flex-shrink: 0;
  font-size: 10px;
  transition: transform 0.2s ease;
  display: inline-block;
}

.date-select-arrow.is-open {
  transform: rotate(180deg);
}

.date-select-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-top: none;
  border-radius: 0 0 6px 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  max-height: 300px;
  overflow-y: auto;
  z-index: 1001;
}

.date-select-option {
  padding: 10px 12px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
  user-select: none;
}

.date-select-option:last-child {
  border-bottom: none;
}

.date-select-option:hover {
  background: #f5f5f5;
  color: #333;
}

.date-select-option.is-selected {
  background: #f8f8f8;
  color: #333;
  font-weight: 500;
}

.map-core {
  display: flex;
  flex-direction: column;
  background: transparent;
  padding: 0;
  height: 100%;
  max-height: calc(100vh - 200px);
  overflow: hidden;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.map-body {
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 100%;
  overflow: hidden;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.zoom-controls {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 8;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  padding: 4px;
}

.zoom-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
}

.zoom-btn:hover:not(:disabled) {
  background: #7ABA1E;
  border-color: #7ABA1E;
  color: #fff;
}

.zoom-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.zoom-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.zoom-btn svg {
  width: 16px;
  height: 16px;
}

.map-tooltip {
  position: absolute;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  width: 300px;
  max-width: 320px;
  max-height: 500px;
  overflow-y: auto;
  padding: 12px 16px;
  z-index: 5;
  pointer-events: none;
}

.map-tooltip .tooltip-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
  color: #333;
}

.map-tooltip .section-title {
  font-size: 12px;
  font-weight: 600;
  color: #2b7bb9;
  margin: 8px 0 6px 0;
}

.map-tooltip .row-item {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: #444;
  line-height: 1.3;
}

.map-tooltip .row-item .item-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.map-tooltip .row-item .item-qty {
  color: #666;
  min-width: 20px;
  text-align: right;
}

.tooltip-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  min-height: 80px;
  color: #666;
  font-size: 12px;
}

.customer-profile-section {
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
}

.gender-bars {
  display: flex;
  gap: 4px;
  margin: 8px 0;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
}

.gender-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  color: white;
  font-size: 10px;
  font-weight: 600;
  min-width: 60px;
  position: relative;
}

.male-bar {
  background-color: #4A90E2;
  border-radius: 6px 0 0 6px;
}

.female-bar {
  background-color: #E94B8B;
  border-radius: 0 6px 6px 0;
}

.gender-icon {
  height: 18px;
  width: auto;
  margin-right: 4px;
}

.gender-info {
  font-size: 9px;
  white-space: nowrap;
}

.age-groups-tooltip {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  justify-content: space-between;
}

.age-group-tooltip {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.age-bars-tooltip {
  display: flex;
  gap: 2px;
  height: 60px;
  align-items: flex-end;
  width: 100%;
  margin-top: 20px;
  padding-top: 4px;
}

.age-bar-tooltip {
  width: 50%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: flex-end;
}

.age-bar-fill {
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 8px;
  border-radius: 2px 2px 0 0;
  color: white;
  font-size: 8px;
  font-weight: 600;
}

.age-value {
  position: absolute;
  top: -20px;
  font-size: 8px;
  white-space: nowrap;
  color: #333;
  margin-top: 4px;
}

.age-label-tooltip {
  margin-top: 20px;
  font-size: 8px;
  color: #666;
  text-align: center;
  line-height: 1.2;
}

.tooltip-loading .spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #d7d7d7;
  border-top-color: #0BA7DA; /* azul da UI */
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
.map-sidebar {
  background: #fff !important;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  padding: 20px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  align-self: flex-start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
}

.sidebar-col {
  display: flex !important;
  visibility: visible !important;
}

.close-button {
  display: none;
}

.title-text {
  font-size: 18px;
  font-weight: bold;
  margin-top: 0;
  margin-bottom: 10px;
}

.sub-title-text {
  font-size: 14px;
  color: #666;
}

.chip {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.chip.green {
  background-color: #7ABA1E;
  color: white;
}

.flex-box {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Flex-box específico para barras de gênero */
.gender-bars-sidebar {
  display: flex;
  gap: 4px;
  margin: 8px 0;
  height: 42px;
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
}

.gender-bar-sidebar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 6px;
  color: white;
  font-size: 11px;
  font-weight: 600;
  position: relative;
  overflow: visible;
  box-sizing: border-box;
}

.male-bar-sidebar {
  background-color: #4A90E2;
  border-radius: 8px 0 0 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.female-bar-sidebar {
  background-color: #E94B8B;
  border-radius: 0 8px 8px 0;
  flex: 0 0 120px;
  min-width: 120px;
}

.gender-icon-sidebar {
  height: 20px;
  width: auto;
  margin-right: 4px;
  flex-shrink: 0;
}

.gender-info-sidebar {
  font-size: 10px;
  white-space: nowrap;
  flex-shrink: 0;
}

.flip {
  cursor: pointer;
  background: none;
  border: none;
  color: #666;
  text-decoration: underline;
  padding: 5px 0;
}

.panel-hidden {
  display: none;
}

.gg-sidebar {
  margin-top: 10px;
}

.male-color {
  background-color: #4A90E2;
}

.female-color {
  background-color: #E94B8B;
}

.gb-sidebar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px 8px;
  color: white;
  font-size: 12px;
  border-radius: 8px;
  min-width: 100px;
  flex-shrink: 0;
  overflow: visible;
  white-space: nowrap;
}

.gt-sidebar {
  margin-left: 5px;
}

.age-pair {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 5px;
}

.pair-container {
  display: flex;
  gap: 4px;
  height: 100px;
  align-items: flex-end;
}

.age-bar {
  width: 20px;
  height: 100px;
  position: relative;
}

@media (min-width: 991px) {
  .age-bar {
    width: 14px;
  }
}

.age {
  width: 100%;
  position: absolute;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 10px;
  min-height: 8px;
  border-radius: 4px 4px 0 0;
}

.age-info {
  position: absolute;
  bottom: -20px;
  font-size: 9px;
  white-space: nowrap;
  color: #333;
}

.rotate-age {
  transform: rotate(-45deg);
  transform-origin: center;
}

.age-container {
  margin-top: 25px;
  text-align: center;
}

.age-label {
  font-size: 10px;
  color: #666;
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.2;
}

.age-label strong {
  font-weight: 600;
  color: #333;
}

.age-label-suffix {
  font-weight: normal;
  font-size: 9px;
  color: #666;
  margin-top: 2px;
}

.small-text {
  font-size: 11px;
}

.normal-text {
  font-size: 12px;
}

hr {
  margin: 15px 0;
  border: none;
  border-top: 1px solid #ddd;
}

ul {
  list-style: none;
  padding: 0;
  margin: 10px 0;
}

ul li {
  padding: 5px 0;
  font-size: 12px;
}

.regions-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.region-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.region-item:hover {
  background: #f0f0f0;
  transform: translateX(2px);
}

.region-item.region-selected {
  background: #e8f4fd;
  border-color: #2b7bb9;
  box-shadow: 0 2px 4px rgba(43, 123, 185, 0.15);
}

.region-color-indicator {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 2px solid #fff;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.region-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.region-name {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}

.region-value {
  font-size: 11px;
  color: #666;
}

.region-check {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #2b7bb9;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
}

.filter-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
  gap: 8px;
}

.remove-filter-btn {
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;
}

.remove-filter-btn:hover {
  background: #c82333;
}

#svg-map .region-highlighted {
  filter: brightness(1.1);
}

/* DatePicker deve ficar abaixo dos controles de zoom */
.date-select-wrapper + date-picker,
.control-group date-picker {
  position: relative;
  z-index: 5;
}

.date-select-wrapper + date-picker ::v-deep .v-picker,
.control-group date-picker ::v-deep .v-picker {
  z-index: 5 !important;
}

@media (max-width: 990px) {
  .desktop-resize-col {
    flex: 0 0 100%;
    max-width: 100%;
  }
}

@media (min-width: 991px) {
  .desktop-resize-col {
    flex: 0 0 63.66666666666666%;
    max-width: 100%;
  }
}

@media (min-width: 1200px) {
  .desktop-resize-col {
    flex: 0 0 64.66666666666666%;
    max-width: 100%;
  }
}
</style>

